// src/stock/ValeurStock.jsx
import React, { useState, useEffect } from "react";
import {
    Card, Row, Col, Table, Button,
    Tooltip, OverlayTrigger
} from "react-bootstrap";
import { useNavigate, Link } from "react-router-dom";
import Footer from "../layouts/Footer";
import Header from "../layouts/Header";
import { formatCurrency } from "../utils/formatters";
import { API_BASE_URL } from "../constants/api";
import { COLORS } from "../constants/colors";

/* ============================================================
   TOAST NOTIFICATION — Design charte
   ============================================================ */
const ToastNotification = ({ show, onClose, type, title, message, autoHideDelay = 5000 }) => {
    if (!show) return null;
    const config = {
        success: { bgColor: COLORS.success || '#10b981', icon: 'ri-checkbox-circle-fill' },
        error:   { bgColor: COLORS.danger  || '#ef4444', icon: 'ri-error-warning-fill' },
        info:    { bgColor: COLORS.info    || '#3b82f6', icon: 'ri-information-fill' },
        warning: { bgColor: COLORS.warning || '#f59e0b', icon: 'ri-alert-fill' }
    }[type] || { bgColor: COLORS.info || '#3b82f6', icon: 'ri-information-fill' };

    return (
        <div className="toast-notification-fixed">
            <div className="toast-modern">
                <div className="toast-header-custom" style={{ background: config.bgColor }}>
                    <div className="d-flex align-items-center gap-2">
                        <i className={`${config.icon} text-white`} style={{ fontSize: '1.5rem' }}></i>
                        <strong className="text-white fs-6">{title}</strong>
                    </div>
                    <button onClick={onClose} className="toast-close-btn">
                        <i className="ri-close-line fs-5"></i>
                    </button>
                </div>
                <div className="toast-body-custom"><p className="mb-0">{message}</p></div>
                <div className="toast-progress" style={{ background: config.bgColor, animation: `shrink ${autoHideDelay}ms linear forwards` }}></div>
            </div>
        </div>
    );
};

/* ============================================================
   COMPOSANT PRINCIPAL
   ============================================================ */
export default function ValeurStock() {
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [viewMode, setViewMode] = useState('global');
    const [toast, setToast] = useState({ show: false, type: '', title: '', message: '' });

    const showToast = (type, title, message) => setToast({ show: true, type, title, message });
    const hideToast = () => setToast({ show: false, type: '', title: '', message: '' });

    useEffect(() => {
        fetchValeurStock();
    }, []);

    /* ============================================================
       FETCH
       ============================================================ */
    const fetchValeurStock = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem("token");
            const response = await fetch(
                `${API_BASE_URL}/stock_etat.php?action=valeur`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            if (!response.ok) throw new Error("Erreur chargement");
            const result = await response.json();
            setData(result.data);
        } catch (err) {
            setError(err.message);
            showToast('error', 'Erreur', err.message);
        } finally {
            setLoading(false);
        }
    };

    /* ============================================================
       LOADING — identique à l'original
       ============================================================ */
    if (loading && !data) {
        return (
            <>
                <Header />
                <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>
                    <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 'calc(100vh - 100px)' }}>
                        <div className="text-center" style={{ maxWidth: '400px' }}>
                            <div className="loading-ring-wrapper mb-4">
                                <div className="loading-ring"></div>
                                <div className="loading-ring-inner"></div>
                                <i className="ri-loader-4-fill loading-icon"></i>
                            </div>
                            <h5 className="fw-bold" style={{ color: COLORS.primaryDark, letterSpacing: '0.3px' }}>
                                Calcul de la valeur du stock...
                            </h5>
                            <p className="text-muted small mb-0">Veuillez patienter quelques instants...</p>
                            <div className="mx-auto mt-4" style={{ width: '80%' }}>
                                <div className="progress" style={{ height: '3px', background: '#e9ecef', borderRadius: '10px', overflow: 'hidden' }}>
                                    <div
                                        className="progress-bar progress-bar-striped progress-bar-animated"
                                        style={{
                                            width: '100%',
                                            background: `linear-gradient(90deg, ${COLORS.primary}, ${COLORS.secondary}, ${COLORS.primary})`,
                                            backgroundSize: '200% 100%',
                                            animation: 'shimmer 1.5s linear infinite'
                                        }}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <Footer />
                <style>{`
                    .loading-ring-wrapper { position: relative; width: 80px; height: 80px; margin: 0 auto; }
                    .loading-ring { position: absolute; top: 0; left: 0; width: 100%; height: 100%; border-radius: 50%; border: 4px solid rgba(33,172,205,0.15); border-top-color: ${COLORS.primary}; animation: spin 1s linear infinite; }
                    .loading-ring-inner { position: absolute; top: 8px; left: 8px; right: 8px; bottom: 8px; border-radius: 50%; border: 3px solid rgba(33,172,205,0.08); border-bottom-color: ${COLORS.secondary}; animation: spin 0.8s linear infinite reverse; }
                    .loading-icon { position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%); font-size: 28px; color: ${COLORS.primary}; animation: pulse-icon 1.2s ease-in-out infinite; }
                    @keyframes spin { to { transform: rotate(360deg); } }
                    @keyframes pulse-icon { 0%,100% { transform: translate(-50%,-50%) scale(1); opacity:1; } 50% { transform: translate(-50%,-50%) scale(1.15); opacity:0.6; } }
                    @keyframes shimmer { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }
                `}</style>
            </>
        );
    }

    if (error || !data) {
        return (
            <>
                <Header />
                <div className="main main-app p-3 p-lg-4">
                    <div className="empty-state-charte">
                        <div className="empty-state-icon-charte" style={{ background: 'rgba(239,68,68,0.1)', color: COLORS.danger }}>
                            <i className="ri-error-warning-line"></i>
                        </div>
                        <h6 className="fw-semibold mb-1">Erreur de chargement</h6>
                        <p className="text-muted small mb-3">{error || "Erreur de chargement des données"}</p>
                        <Button className="btn-modern-outline" onClick={fetchValeurStock}>
                            <i className="ri-refresh-line me-1"></i>Réessayer
                        </Button>
                    </div>
                </div>
                <Footer />
            </>
        );
    }

    const valeurMoyenne = (data.global?.valeur_achat || 0) / (data.global?.total_articles || 1);
    const margePotentielle = (data.global?.valeur_vente || 0) - (data.global?.valeur_achat || 0);
    const tauxMarge = ((margePotentielle / (data.global?.valeur_achat || 1)) * 100).toFixed(1);

    return (
        <>
            <Header />
            <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>

                <ToastNotification
                    show={toast.show}
                    onClose={hideToast}
                    type={toast.type}
                    title={toast.title}
                    message={toast.message}
                />

                <div className="container-fluid">

                    {/* ===== EN-TÊTE MODERNE ===== */}
                    <div className="header-modern mb-4">
                        <div className="header-left">
                            <div className="header-icon-wrapper">
                                <i className="ri-pie-chart-2-line"></i>
                            </div>
                            <div>
                                <h5 className="header-title">Valeur du stock</h5>
                                <div className="d-flex flex-wrap gap-2 mt-1">
                                    <span className="badge-modern">
                                        <i className="ri-stack-line"></i> {data.global?.total_produits || 0} produits
                                    </span>
                                    <span className="badge-modern" style={{ background: `${COLORS.success}15`, color: COLORS.success }}>
                                        <i className="ri-money-euro-circle-line"></i> {formatCurrency(data.global?.valeur_achat || 0)}
                                    </span>
                                </div>
                            </div>
                        </div>
                        <div className="d-flex gap-2 flex-wrap">
                            <Button className="btn-modern-outline" onClick={fetchValeurStock} disabled={loading}>
                                <i className={`ri-refresh-line ${loading ? 'spin-icon' : ''}`}></i> Actualiser
                            </Button>
                            <Button className="btn-modern-outline" onClick={() => navigate('/stock/etat/instantane')}>
                                <i className="ri-arrow-left-line"></i> Retour
                            </Button>
                        </div>
                    </div>

                    {/* ===== VALEUR CARDS ===== */}
                    <Row className="g-3 mb-4">
                        <Col lg="6">
                            <div className="value-card value-card-primary">
                                <div className="value-card-content">
                                    <div className="value-card-badge">
                                        <i className="ri-shopping-cart-line me-1"></i>
                                        Prix d'achat
                                    </div>
                                    <h4 className="value-card-amount">{formatCurrency(data.global?.valeur_achat || 0)}</h4>
                                    <p className="value-card-footer">Coût total d'acquisition</p>
                                </div>
                                <div className="value-card-icon">
                                    <i className="ri-shopping-cart-line"></i>
                                </div>
                            </div>
                        </Col>
                        <Col lg="6">
                            <div className="value-card value-card-success">
                                <div className="value-card-content">
                                    <div className="value-card-badge">
                                        <i className="ri-money-euro-circle-line me-1"></i>
                                        Prix de vente
                                    </div>
                                    <h4 className="value-card-amount">{formatCurrency(data.global?.valeur_vente || 0)}</h4>
                                    <p className="value-card-footer">Valeur de revente potentielle</p>
                                </div>
                                <div className="value-card-icon">
                                    <i className="ri-money-euro-circle-line"></i>
                                </div>
                            </div>
                        </Col>
                    </Row>

                    {/* ===== KPI CARDS ===== */}
                    <Row className="g-3 mb-4">
                        <Col sm="6" xl="4">
                            <div className="kpi-charte kpi-primary">
                                <div className="kpi-icon-charte"><i className="ri-stack-line"></i></div>
                                <div className="kpi-content-charte">
                                    <span className="kpi-label-charte">Total produits</span>
                                    <h3 className="kpi-value-charte">{data.global?.total_produits || 0}</h3>
                                    <span className="kpi-unit-charte">références actives</span>
                                </div>
                            </div>
                        </Col>
                        <Col sm="6" xl="4">
                            <div className="kpi-charte kpi-info">
                                <div className="kpi-icon-charte"><i className="ri-calculator-line"></i></div>
                                <div className="kpi-content-charte">
                                    <span className="kpi-label-charte">Valeur moyenne</span>
                                    <h3 className="kpi-value-charte" style={{ fontSize: '1rem' }}>
                                        {formatCurrency(valeurMoyenne)}
                                    </h3>
                                    <span className="kpi-unit-charte">par article</span>
                                </div>
                            </div>
                        </Col>
                        <Col sm="6" xl="4">
                            <div className="kpi-charte kpi-success">
                                <div className="kpi-icon-charte"><i className="ri-line-chart-line"></i></div>
                                <div className="kpi-content-charte">
                                    <span className="kpi-label-charte">Marge potentielle</span>
                                    <h3 className="kpi-value-charte" style={{ fontSize: '1rem' }}>
                                        {formatCurrency(margePotentielle)}
                                    </h3>
                                    <span className="kpi-unit-charte">taux {tauxMarge}%</span>
                                </div>
                            </div>
                        </Col>
                    </Row>

                    {/* ===== VIEW TOGGLE ===== */}
                    <div className="view-toggle-container-charte mb-4">
                        <button
                            className={`view-toggle-btn-charte ${viewMode === 'global' ? 'active' : ''}`}
                            onClick={() => setViewMode('global')}
                        >
                            <i className="ri-pie-chart-2-line me-1"></i> Global
                        </button>
                        <button
                            className={`view-toggle-btn-charte ${viewMode === 'details' ? 'active' : ''}`}
                            onClick={() => setViewMode('details')}
                        >
                            <i className="ri-list-check-line me-1"></i> Détails
                        </button>
                    </div>

                    {/* ===== VUE PAR CATÉGORIE ===== */}
                    {viewMode === 'global' && (
                        <Card className="border-0 shadow-sm rounded-3 overflow-hidden">
                            <Card.Header className="card-header-gradient">
                                <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                                    <div className="d-flex align-items-center gap-2">
                                        <i className="ri-folder-chart-line text-white fs-5"></i>
                                        <span className="fw-semibold text-white">Répartition par catégorie</span>
                                        <span className="badge-header-light">{data.par_categorie?.length || 0} catégories</span>
                                    </div>
                                </div>
                            </Card.Header>
                            <Card.Body className="p-0">
                                <div className="table-responsive">
                                    <Table hover className="align-middle mb-0 table-charte">
                                        <thead>
                                            <tr>
                                                <th className="ps-4">Catégorie</th>
                                                <th className="text-center">Produits</th>
                                                <th className="text-center">Articles</th>
                                                <th className="text-end">Valeur achat</th>
                                                <th className="text-end">Valeur vente</th>
                                                <th className="pe-4" style={{ width: '200px' }}>Répartition</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {data.par_categorie?.map((c, index) => {
                                                const pourcentage = (c.valeur_achat / data.global?.valeur_achat) * 100;
                                                return (
                                                    <tr key={index}>
                                                        <td className="ps-4">
                                                            <div className="d-flex align-items-center gap-2">
                                                                <div className="avatar-icon-charte-small">
                                                                    <i className="ri-folder-line"></i>
                                                                </div>
                                                                <span className="fw-semibold">
                                                                    {c.nom_categorie || 'Sans catégorie'}
                                                                </span>
                                                            </div>
                                                        </td>
                                                        <td className="text-center">
                                                            <span className="quantity-badge-charte">{c.nb_produits}</span>
                                                        </td>
                                                        <td className="text-center">
                                                            <span className="fw-semibold">{c.total_articles}</span>
                                                        </td>
                                                        <td className="text-end fw-bold" style={{ color: COLORS.primary }}>
                                                            {formatCurrency(c.valeur_achat)}
                                                        </td>
                                                        <td className="text-end fw-semibold" style={{ color: COLORS.success }}>
                                                            {formatCurrency(c.valeur_vente)}
                                                        </td>
                                                        <td className="pe-4">
                                                            <div className="d-flex align-items-center gap-2">
                                                                <div className="progress-bar-charte" style={{ width: '120px' }}>
                                                                    <div className="progress-fill-charte"
                                                                         style={{ width: `${pourcentage}%`, background: COLORS.primary }}>
                                                                    </div>
                                                                </div>
                                                                <small className="text-muted">{pourcentage.toFixed(1)}%</small>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </Table>
                                </div>
                            </Card.Body>
                        </Card>
                    )}

                    {/* ===== VUE DÉTAILS ===== */}
                    {viewMode === 'details' && (
                        <>
                            <Card className="border-0 shadow-sm rounded-3 overflow-hidden mb-4">
                                <Card.Header className="card-header-gradient">
                                    <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                                        <div className="d-flex align-items-center gap-2">
                                            <i className="ri-trophy-line text-white fs-5"></i>
                                            <span className="fw-semibold text-white">Top 10 produits par valeur</span>
                                            <span className="badge-header-light">Plus haute valeur</span>
                                        </div>
                                    </div>
                                </Card.Header>
                                <Card.Body className="p-0">
                                    <div className="table-responsive">
                                        <Table hover className="align-middle mb-0 table-charte">
                                            <thead>
                                                <tr>
                                                    <th className="ps-4">Réf.</th>
                                                    <th>Produit</th>
                                                    <th className="text-center">Qté</th>
                                                    <th className="text-end">Prix achat</th>
                                                    <th className="text-end">Prix vente</th>
                                                    <th className="text-end">Valeur achat</th>
                                                    <th className="text-end">Valeur vente</th>
                                                    <th className="text-center pe-4">Actions</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {data.top_produits?.map((p, index) => (
                                                    <tr key={index}>
                                                        <td className="ps-4">
                                                            <span className="reference-badge-charte">{p.reference}</span>
                                                        </td>
                                                        <td>
                                                            <Link to={`/stock/produits/${p.id_produit}`} className="table-link-charte">
                                                                {p.designation}
                                                            </Link>
                                                        </td>
                                                        <td className="text-center">
                                                            <span className="quantity-badge-charte">{p.quantite_reelle}</span>
                                                        </td>
                                                        <td className="text-end" style={{ color: COLORS.primary }}>
                                                            {formatCurrency(p.prix_achat_ht)}
                                                        </td>
                                                        <td className="text-end" style={{ color: COLORS.success }}>
                                                            {formatCurrency(p.prix_vente_ht)}
                                                        </td>
                                                        <td className="text-end fw-bold" style={{ color: COLORS.primary }}>
                                                            {formatCurrency(p.valeur_achat)}
                                                        </td>
                                                        <td className="text-end fw-semibold" style={{ color: COLORS.success }}>
                                                            {formatCurrency(p.valeur_vente)}
                                                        </td>
                                                        <td className="text-center pe-4">
                                                            <OverlayTrigger placement="top" overlay={<Tooltip>Voir le produit</Tooltip>}>
                                                                <button
                                                                    className="action-btn-charte"
                                                                    onClick={() => navigate(`/stock/produits/${p.id_produit}`)}
                                                                    style={{ '--btn-color': COLORS.primary }}
                                                                >
                                                                    <i className="ri-eye-line"></i>
                                                                </button>
                                                            </OverlayTrigger>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </Table>
                                    </div>
                                </Card.Body>
                            </Card>

                            {/* Graphique de répartition */}
                            <Card className="border-0 shadow-sm rounded-3">
                                <Card.Header className="card-header-gradient">
                                    <div className="d-flex align-items-center gap-2">
                                        <i className="ri-pie-chart-2-line text-white fs-5"></i>
                                        <span className="fw-semibold text-white">Répartition de la valeur par catégorie</span>
                                    </div>
                                </Card.Header>
                                <Card.Body className="p-4">
                                    <Row className="g-4">
                                        {data.par_categorie?.slice(0, 5).map((c, index) => {
                                            const pourcentage = (c.valeur_achat / data.global?.valeur_achat) * 100;
                                            const colors = [COLORS.primary, '#f59e0b', '#10b981', '#ef4444', '#8b5cf6'];
                                            return (
                                                <Col md={6} key={index}>
                                                    <div className="mb-3">
                                                        <div className="d-flex justify-content-between mb-1">
                                                            <span className="text-muted small">
                                                                {c.nom_categorie || 'Sans catégorie'}
                                                            </span>
                                                            <span className="fw-semibold">
                                                                {formatCurrency(c.valeur_achat)}
                                                            </span>
                                                        </div>
                                                        <div className="progress-bar-charte" style={{ width: '100%' }}>
                                                            <div className="progress-fill-charte"
                                                                 style={{
                                                                     width: `${pourcentage}%`,
                                                                     background: colors[index % colors.length]
                                                                 }}>
                                                            </div>
                                                        </div>
                                                        <small className="text-muted float-end mt-1">
                                                            {pourcentage.toFixed(1)}%
                                                        </small>
                                                    </div>
                                                </Col>
                                            );
                                        })}
                                    </Row>
                                </Card.Body>
                            </Card>
                        </>
                    )}
                </div>
                <Footer />
            </div>

            {/* ===== STYLES CHARTE ===== */}
            <style>{`
                :root {
                    --primary: ${COLORS.primary || '#295AA7'};
                    --primary-dark: ${COLORS.primaryDark || '#1e3f7a'};
                    --secondary: ${COLORS.secondary || '#21ACCD'};
                    --success: ${COLORS.success || '#10b981'};
                    --danger: ${COLORS.danger || '#ef4444'};
                    --warning: ${COLORS.warning || '#f59e0b'};
                    --info: ${COLORS.info || '#3b82f6'};
                }

                /* ===== EN-TÊTE ===== */
                .header-modern {
                    background: white; padding: 1.2rem 1.8rem;
                    box-shadow: 0 2px 8px rgba(0,0,0,0.04);
                    border-left: 4px solid var(--secondary);
                    border-radius: 12px;
                    display: flex; align-items: center; justify-content: space-between;
                    flex-wrap: wrap; gap: 1rem;
                }
                .header-left { display: flex; align-items: center; gap: 1rem; }
                .header-icon-wrapper {
                    width: 52px; height: 52px;
                    background: linear-gradient(135deg, var(--primary), var(--secondary));
                    display: flex; align-items: center; justify-content: center;
                    border-radius: 14px; color: white; font-size: 1.5rem;
                    box-shadow: 0 4px 12px rgba(41, 90, 167, 0.25);
                }
                .header-title { color: #1e293b; font-size: 1.3rem; font-weight: 700; margin: 0; }
                .badge-modern {
                    background: #f1f4f9; color: #475569;
                    padding: 0.3rem 0.8rem; border-radius: 50px;
                    font-size: 0.75rem; font-weight: 600;
                    display: inline-flex; align-items: center; gap: 6px;
                }
                .badge-modern i { color: var(--secondary); }
                .spin-icon { animation: spin 1s linear infinite; }

                /* ===== VALUE CARDS ===== */
                .value-card {
                    border-radius: 16px;
                    padding: 1rem 1.25rem;
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    transition: all 0.3s ease;
                    height: 100%;
                    min-height: 90px;
                }
                .value-card:hover {
                    transform: translateY(-3px);
                    box-shadow: 0 12px 20px -8px rgba(0,0,0,0.15);
                }
                .value-card-primary {
                    background: linear-gradient(135deg, ${COLORS.primaryDark}, ${COLORS.primary});
                }
                .value-card-success {
                    background: linear-gradient(135deg, #10b981, #059669);
                }
                .value-card-content { flex: 1; }
                .value-card-badge {
                    display: inline-block;
                    background: rgba(255,255,255,0.2);
                    padding: 0.15rem 0.8rem;
                    border-radius: 30px;
                    font-size: 0.6rem;
                    font-weight: 600;
                    color: white;
                    margin-bottom: 0.3rem;
                    letter-spacing: 0.3px;
                    text-transform: uppercase;
                }
                .value-card-amount {
                    font-size: 1.25rem;
                    font-weight: 700;
                    color: white;
                    margin: 0;
                    line-height: 1.2;
                }
                .value-card-footer {
                    font-size: 0.6rem;
                    color: rgba(255,255,255,0.7);
                    margin: 0;
                }
                .value-card-icon {
                    width: 40px;
                    height: 40px;
                    background: rgba(255,255,255,0.2);
                    border-radius: 12px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    flex-shrink: 0;
                    margin-left: 0.75rem;
                }
                .value-card-icon i {
                    font-size: 1.25rem;
                    color: white;
                }

                /* ===== KPI ===== */
                .kpi-charte {
                    background: white; border-radius: 12px;
                    padding: 1.2rem;
                    display: flex; align-items: center; gap: 1rem;
                    box-shadow: 0 2px 8px rgba(0,0,0,0.04);
                    border-left: 4px solid;
                    transition: all 0.25s ease;
                    height: 100%;
                }
                .kpi-charte:hover {
                    transform: translateY(-3px);
                    box-shadow: 0 12px 28px rgba(0,0,0,0.08);
                }
                .kpi-primary { border-left-color: var(--primary); }
                .kpi-success { border-left-color: var(--success); }
                .kpi-danger  { border-left-color: var(--danger); }
                .kpi-info    { border-left-color: var(--info); }
                .kpi-warning { border-left-color: var(--warning); }
                .kpi-icon-charte {
                    width: 48px; height: 48px; border-radius: 12px;
                    display: flex; align-items: center; justify-content: center;
                    font-size: 1.4rem; flex-shrink: 0;
                }
                .kpi-primary .kpi-icon-charte { background: ${COLORS.primary}15; color: var(--primary); }
                .kpi-success .kpi-icon-charte { background: ${COLORS.success}15; color: var(--success); }
                .kpi-danger  .kpi-icon-charte { background: ${COLORS.danger}15; color: var(--danger); }
                .kpi-info    .kpi-icon-charte { background: ${COLORS.info}15; color: var(--info); }
                .kpi-warning .kpi-icon-charte { background: ${COLORS.warning}15; color: var(--warning); }
                .kpi-content-charte { flex: 1; min-width: 0; }
                .kpi-label-charte {
                    font-size: 0.7rem; color: #94a3b8;
                    text-transform: uppercase; font-weight: 600;
                    letter-spacing: 0.5px;
                }
                .kpi-value-charte {
                    font-size: 1.35rem; font-weight: 700;
                    margin: 0.3rem 0; color: #1e293b;
                }
                .kpi-unit-charte { font-size: 0.7rem; color: #94a3b8; }

                /* ===== VIEW TOGGLE ===== */
                .view-toggle-container-charte {
                    display: inline-flex;
                    gap: 4px;
                    padding: 4px;
                    background: white;
                    border-radius: 50px;
                    box-shadow: 0 2px 8px rgba(0,0,0,0.04);
                    border: 1.5px solid #e2e8f0;
                }
                .view-toggle-btn-charte {
                    padding: 0.5rem 1.25rem;
                    border-radius: 50px;
                    border: none;
                    background: transparent;
                    color: #64748b;
                    font-weight: 600;
                    font-size: 0.85rem;
                    transition: all 0.2s ease;
                    cursor: pointer;
                    display: inline-flex;
                    align-items: center;
                }
                .view-toggle-btn-charte:hover {
                    color: var(--primary);
                }
                .view-toggle-btn-charte.active {
                    background: linear-gradient(135deg, var(--primary), var(--primary-dark));
                    color: white;
                    box-shadow: 0 4px 12px rgba(41,90,167,0.3);
                }

                /* ===== HEADER GRADIENT ===== */
                .card-header-gradient {
                    background: linear-gradient(135deg, var(--primary) 0%, var(--primary-dark) 100%);
                    border-top-left-radius: 12px !important;
                    border-top-right-radius: 12px !important;
                    padding: 1rem 1.5rem;
                    border: none;
                }
                .badge-header-light {
                    background: rgba(255,255,255,0.2);
                    color: white;
                    border: 1px solid rgba(255,255,255,0.3);
                    border-radius: 50px;
                    padding: 0.25rem 0.75rem;
                    font-size: 0.75rem;
                    font-weight: 600;
                }

                /* ===== TABLE ===== */
                .table-charte thead th {
                    background: #f8fafc; color: #475569;
                    font-weight: 600; font-size: 0.7rem;
                    text-transform: uppercase; letter-spacing: 0.5px;
                    border-bottom: 2px solid #e2e8f0;
                    padding: 0.9rem 0.75rem;
                }
                .table-charte tbody td {
                    padding: 0.9rem 0.75rem;
                    vertical-align: middle;
                    border-bottom: 1px solid #f1f5f9;
                    font-size: 0.85rem;
                }
                .table-charte tbody tr { transition: background 0.15s ease; }
                .table-charte tbody tr:hover { background: #f8fafc; }
                .table-link-charte {
                    color: var(--primary);
                    text-decoration: none;
                    font-weight: 600;
                    font-size: 0.85rem;
                }
                .table-link-charte:hover { text-decoration: underline; }
                .reference-badge-charte {
                    display: inline-flex; align-items: center; gap: 4px;
                    background: #f1f5f9; padding: 0.25rem 0.6rem;
                    border-radius: 6px; font-size: 0.7rem;
                    font-family: monospace; color: #475569;
                }
                .quantity-badge-charte {
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    min-width: 50px;
                    padding: 0.25rem 0.5rem;
                    background: #f1f5f9;
                    border-radius: 12px;
                    font-weight: 600;
                    font-size: 0.85rem;
                    color: #1e293b;
                }
                .avatar-icon-charte-small {
                    width: 32px; height: 32px;
                    background: ${COLORS.primary}15;
                    border-radius: 50%;
                    display: flex; align-items: center; justify-content: center;
                    color: var(--primary);
                }

                /* ===== PROGRESS ===== */
                .progress-bar-charte {
                    background: #e2e8f0;
                    border-radius: 4px;
                    height: 6px;
                    overflow: hidden;
                }
                .progress-fill-charte {
                    height: 100%;
                    border-radius: 4px;
                    transition: width 0.3s ease;
                }

                /* ===== ACTION BTN ===== */
                .action-btn-charte {
                    width: 36px; height: 36px; border-radius: 10px;
                    display: inline-flex; align-items: center; justify-content: center;
                    background: white; border: 1.5px solid #e2e8f0;
                    color: #64748b; font-size: 1rem;
                    transition: all 0.2s ease;
                    cursor: pointer;
                }
                .action-btn-charte:hover {
                    border-color: var(--btn-color); color: var(--btn-color);
                    transform: translateY(-2px);
                    box-shadow: 0 6px 16px rgba(41, 90, 167, 0.15);
                }

                /* ===== EMPTY STATE ===== */
                .empty-state-charte { text-align: center; padding: 3rem 2rem; }
                .empty-state-icon-charte {
                    width: 72px; height: 72px;
                    background: linear-gradient(135deg, #f1f5f9, #e2e8f0);
                    border-radius: 50%;
                    display: flex; align-items: center; justify-content: center;
                    margin: 0 auto 1rem; font-size: 1.8rem; color: #94a3b8;
                }

                /* ===== BOUTONS ===== */
                .btn-modern-outline {
                    border: 1.5px solid #d1d5db; background: white;
                    padding: 0.5rem 1.4rem; border-radius: 50px;
                    font-size: 0.85rem; font-weight: 500; color: #4b5563;
                    transition: all 0.2s ease;
                    display: inline-flex; align-items: center; gap: 6px;
                }
                .btn-modern-outline:hover {
                    border-color: var(--secondary); color: var(--secondary);
                    background: #f0fbff; transform: translateY(-1px);
                    box-shadow: 0 4px 8px rgba(33,172,205,0.15);
                }
                .btn-modern-outline:disabled { opacity: 0.6; transform: none; }

                /* ===== TOAST ===== */
                .toast-notification-fixed {
                    position: fixed; bottom: 24px; right: 24px;
                    z-index: 9999; animation: slideInRight 0.3s ease-out;
                }
                .toast-modern {
                    background: white; border-radius: 16px;
                    box-shadow: 0 20px 40px rgba(0,0,0,0.15);
                    min-width: 320px; max-width: 400px; overflow: hidden;
                }
                .toast-header-custom {
                    padding: 12px 20px;
                    display: flex; align-items: center; justify-content: space-between;
                }
                .toast-close-btn {
                    background: transparent; border: none;
                    color: white; cursor: pointer; opacity: 0.8;
                }
                .toast-close-btn:hover { opacity: 1; }
                .toast-body-custom { padding: 16px 20px; }
                .toast-body-custom p { color: #1e293b; font-size: 0.9rem; }
                .toast-progress { height: 3px; width: 100%; animation: shrink linear forwards; }

                /* ===== ANIMATIONS ===== */
                @keyframes spin { to { transform: rotate(360deg); } }
                @keyframes slideInRight {
                    from { opacity: 0; transform: translateX(100%); }
                    to { opacity: 1; transform: translateX(0); }
                }
                @keyframes shrink {
                    from { width: 100%; }
                    to { width: 0%; }
                }

                /* ===== RESPONSIVE ===== */
                @media (max-width: 768px) {
                    .header-modern { padding: 1rem; }
                    .header-icon-wrapper { width: 44px; height: 44px; font-size: 1.2rem; }
                    .header-title { font-size: 1.1rem; }
                    .toast-notification-fixed { bottom: 12px; right: 12px; left: 12px; }
                    .toast-modern { min-width: auto; max-width: none; }
                    .view-toggle-container-charte { width: 100%; justify-content: center; }
                }
                .rounded-3 { border-radius: 12px !important; }
            `}</style>
        </>
    );
}